import { useEffect, useState } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';

interface AuditEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  module: string;
  object: string;
  details?: {
    anciennes_valeurs?: any;
    nouvelles_valeurs?: any;
    ip_address?: string;
    user_agent?: string;
    url?: string;
    methode_http?: string;
  } | null;
}

interface Meta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export default function AuditLog() {

  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false); // ← nouveau

  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [page, setPage] = useState(1);

  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);

  /* ================= FETCH LISTE ================= */

  const fetchLogs = async () => {
    setLoading(true);
    const res = await getResources(`admin/audit-logs`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      params: {
        search: searchTerm || undefined,
        action: filterAction !== 'all' ? filterAction : undefined,
        module: filterModule !== 'all' ? filterModule : undefined,
        utilisateur: filterUser !== 'all' ? filterUser : undefined,
        page,
      },
    });

    setAuditEntries(res.data.data);
    setMeta(res.data.meta);
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
  }, [searchTerm, filterAction, filterModule, filterUser, page]);

  /* ================= FETCH DÉTAILS ================= */

  const fetchDetails = async (id: string) => {
    setLoadingDetails(true);
    try {
      const res = await getResources(`admin/audit-logs/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });

      // On remplace l'entrée sélectionnée avec les détails complets
      setSelectedEntry(res.data);
    } catch (error) {
      console.error("Erreur lors du chargement des détails", error);
      alert("Impossible de charger les détails de cet audit.");
    } finally {
      setLoadingDetails(false);
    }
  };

  const openDetails = (entry: AuditEntry) => {
    setSelectedEntry(entry); // Afficher le modal immédiatement avec les infos de base
    fetchDetails(entry.id);   // Charger les détails en parallèle
  };

  /* ================= UTILS ================= */

  const getActionColor = (action: string) => {
    switch (action) {
      case 'CREATION': return 'bg-green-100 text-green-800';
      case 'MODIFICATION': return 'bg-blue-100 text-blue-800';
      case 'SUPPRESSION': return 'bg-red-100 text-red-800';
      case 'VALIDATION': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATION': return 'ri-add-circle-line';
      case 'MODIFICATION': return 'ri-edit-line';
      case 'SUPPRESSION': return 'ri-delete-bin-line';
      case 'VALIDATION': return 'ri-checkbox-circle-line';
      default: return 'ri-information-line';
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Utilisateur', 'Action', 'Module', 'Objet'];
    const rows = auditEntries.map(e => [
      e.timestamp,
      e.user,
      e.action,
      e.module,
      e.object,
    ]);

    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
  };

  const countByAction = (action: string) =>
    auditEntries.filter(e => e.action === action).length;

  /* ================= RENDER ================= */

  return (
    <div className="space-y-6">

      {/* Filtres */}
      <div className="flex flex-col lg:flex-row gap-4">
        <input
          className="px-4 py-2 border rounded-lg w-full"
          placeholder="Recherche..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />

        <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="px-4 py-2 border rounded-lg">
          <option value="all">Toutes les actions</option>
          <option value="CREATION">Création</option>
          <option value="MODIFICATION">Modification</option>
          <option value="SUPPRESSION">Suppression</option>
          <option value="VALIDATION">Validation</option>
        </select>

        <button
          onClick={exportToCSV}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
        >
          <i className="ri-download-line" />
          Exporter
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {['CREATION','MODIFICATION','VALIDATION','SUPPRESSION'].map(a => (
          <div key={a} className="bg-white p-4 border rounded-lg">
            <p className="text-sm text-gray-500">{a}</p>
            <p className="text-2xl font-bold">{countByAction(a)}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">Date</th>
              <th className="px-6 py-3 text-left">Utilisateur</th>
              <th className="px-6 py-3 text-left">Action</th>
              <th className="px-6 py-3 text-left">Module</th>
              <th className="px-6 py-3 text-left">Objet</th>
              <th className="px-6 py-3 text-left">Voir</th>
            </tr>
          </thead>
          <tbody>
            {!loading && auditEntries.map(entry => (
              <tr key={entry.id} className="border-t hover:bg-gray-50">
                <td className="px-6 py-4">{entry.timestamp}</td>
                <td className="px-6 py-4">{entry.user}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs ${getActionColor(entry.action)}`}>
                    <i className={getActionIcon(entry.action)} /> {entry.action}
                  </span>
                </td>
                <td className="px-6 py-4">{entry.module}</td>
                <td className="px-6 py-4 font-medium">{entry.object}</td>
                <td className="px-6 py-4">
                  <button onClick={() => openDetails(entry)}>
                    <i className="ri-eye-line text-blue-600 text-lg" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && <div className="p-6 text-center text-gray-500">Chargement...</div>}
      </div>

      {/* Pagination */}
      {meta && (
        <div className="flex justify-between items-center">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-4 py-2 border rounded">Précédent</button>
          <span>Page {meta.current_page} / {meta.last_page}</span>
          <button disabled={page === meta.last_page} onClick={() => setPage(p => p + 1)} className="px-4 py-2 border rounded">Suivant</button>
        </div>
      )}

      {/* Modal détails */}
      {selectedEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">

            {/* Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg sm:text-xl font-bold">
                Détails de l'audit
              </h3>
              <button
                onClick={() => setSelectedEntry(null)}
                className="text-gray-500 hover:text-gray-800 text-xl"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {loadingDetails ? (
                <p className="text-center text-gray-500">
                  Chargement des détails...
                </p>
              ) : selectedEntry.details ? (
                <div className="space-y-6">

                  {/* Infos supplémentaires */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div><strong>IP :</strong> {selectedEntry.details.ip_address || 'N/A'}</div>
                    <div><strong>Méthode HTTP :</strong> {selectedEntry.details.methode_http || 'N/A'}</div>

                    <div className="sm:col-span-2">
                      <strong>URL :</strong>
                      <p className="break-all text-xs text-gray-700 mt-1">
                        {selectedEntry.details.url || 'N/A'}
                      </p>
                    </div>

                    <div className="sm:col-span-2">
                      <strong>User Agent :</strong>
                      <p className="break-all text-xs text-gray-600 mt-1">
                        {selectedEntry.details.user_agent || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Anciennes valeurs */}
                  {selectedEntry.details.anciennes_valeurs &&
                    Object.keys(selectedEntry.details.anciennes_valeurs).length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 text-sm">
                          Anciennes valeurs
                        </h4>
                        <pre className="bg-gray-100 p-4 rounded-lg text-xs overflow-x-auto">
                          {JSON.stringify(
                            selectedEntry.details.anciennes_valeurs,
                            null,
                            2
                          )}
                        </pre>
                      </div>
                    )}

                  {/* Nouvelles valeurs */}
                  {selectedEntry.details.nouvelles_valeurs &&
                    Object.keys(selectedEntry.details.nouvelles_valeurs).length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 text-sm">
                          Nouvelles valeurs
                        </h4>
                        <pre className="bg-gray-100 p-4 rounded-lg text-xs overflow-x-auto">
                          {JSON.stringify(
                            selectedEntry.details.nouvelles_valeurs,
                            null,
                            2
                          )}
                        </pre>
                      </div>
                    )}

                  {/* Aucun changement */}
                  {(!selectedEntry.details.anciennes_valeurs ||
                    Object.keys(selectedEntry.details.anciennes_valeurs).length === 0) &&
                    (!selectedEntry.details.nouvelles_valeurs ||
                      Object.keys(selectedEntry.details.nouvelles_valeurs).length === 0) && (
                      <p className="text-gray-500 italic text-sm">
                        Aucun changement de valeurs enregistré.
                      </p>
                    )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">
                  Aucun détail disponible pour cette action.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-end">
              <button
                onClick={() => setSelectedEntry(null)}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                Fermer
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}